import { EncodingHelper } from '@rightship-tech/common-ui/helpers/encoding.helper';
import { GoogleAnalyticsService } from '@rightship-tech/common-ui/services/analytics/google-analytics.service';
import { GoogleAnalyticsHelper } from '@rightship-tech/common-ui/helpers/google-analytics.helper';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { PLATFORM, autoinject } from "aurelia-framework";
import { RouteConfig, Router, RouterConfiguration } from "aurelia-router";
import { RouteGenerator } from "@rightship-tech/common-ui/route-generator";
import { Messages } from "@rightship-tech/common-ui/constants/event-messages.constants";
import { UserClientService } from '@rightship-tech/common-ui/services/basic/user-client.service';
import { MyVesselsRouteConstants } from '../../../constants/route.constants';
import { CookieHelper } from '@rightship-tech/common-ui/helpers/cookie-helper';
import { TaskService } from '@rightship-tech/tasks-ui/services/task.service';
import { SyncTaskService } from '@rightship-tech/tasks-ui/services/synctask.service';
import { MyVesselService } from '../../../services/my-vessel.service';
import { CacheClientService, CacheLocation } from '@rightship-tech/common-ui/services/basic/client-caching.service';
import { ConfigService } from '@rightship-tech/common-ui/services/basic/config.service';
import { EntityTypes } from '@rightship-tech/common-ui/models/basic.models';
import { GetRightShipQiUserResponseUser } from '@rightship-tech/common-ui/models/user.models';
import { IntercomService } from "@rightship-tech/common-ui/services/analytics/intercom.service";
import { DocumentManagementRoutes } from '@rightship-tech/document-management-app/constants/route.constant';

@autoinject
export class Layout {

    public currentRoute: RouteConfig;
    public showDatePicker: boolean = false;
    private subscriptions: Subscription[] = [];

    constructor(private routeGenerator: RouteGenerator, public userClientService: UserClientService, private configService: ConfigService,
        private eventAggregator: EventAggregator, private googleAnalyticsService: GoogleAnalyticsService, private taskService: TaskService,
        private myVesselService: MyVesselService, private cacheService: CacheClientService, private syncTaskService: SyncTaskService,
        private intercomService: IntercomService) {

        //Subscribe to show date picker event
        this.subscriptions.push(eventAggregator.subscribe(Messages.showDatePicker, () => {
            this.showDatePicker = true;
        }));

        //Subscribe to hide date picker event
        this.subscriptions.push(eventAggregator.subscribe(Messages.hideDatePicker, () => {
            this.showDatePicker = false;
        }));

        //Subscribe all events for Google Analytics tracking
        GoogleAnalyticsHelper.subscribeEvents(this.subscriptions, this.googleAnalyticsService, this.eventAggregator);
    }

    async activate(params: any) {
        var encryptedVesselId = params?.ev;
        var encryptedGuid = params?.eg;
        var encryptedSubCorrespondenceId = params?.esc;
        let encryptedEntityId = params?.eEnityId;
        let isForm = params?.isForm as boolean;

        //After login, get query string params from cookie and decode them
        var key = `queryString-${this.configService.config.environment}`;
        var queryString = CookieHelper.readCookie(key);
        if (!queryString || queryString == "") {
            queryString = window.location.search;
        }

        if (queryString && queryString != "") {

            const urlParams = new URLSearchParams(queryString);
            if (urlParams.get("ev") && urlParams.get("ev") != "") encryptedVesselId = EncodingHelper.decodeUrl(urlParams.get("ev"));
            if (urlParams.get("eg") && urlParams.get("eg") != "") encryptedGuid = EncodingHelper.decodeUrl(urlParams.get("eg"));
            if (urlParams.get("esc") && urlParams.get("esc") != "") encryptedSubCorrespondenceId = EncodingHelper.decodeUrl(urlParams.get("esc"));
            if (urlParams.get("eEnityId") && urlParams.get("eEnityId") != "") encryptedEntityId = EncodingHelper.decodeUrl(urlParams.get("eEnityId"));
            if (urlParams.get("isForm") && urlParams.get("isForm") != "") isForm = EncodingHelper.decodeUrl(urlParams.get("isForm")) as unknown as boolean;
            
            //If path doesn't have RFI correspondence page, delete cookie
            if (!window.location.pathname.includes("rfi-correspondence")) {

                //Delete cookie
                CookieHelper.eraseCookie(key, this.configService.config.environment);
            }
        }

        //Add vessel to My Vessels
        if (this.userClientService.currentUser?.user && encryptedVesselId) {
            var response = await this.myVesselService.addMyVessels([{
                encryptedVesselId: encryptedVesselId
            }]);
            if (response?.length > 0) {
                var vesselIds = response.map((vessel) => vessel.vesselId);
                await this.cacheService.put("filter-vessel-ids", vesselIds, CacheLocation.SessionStorage);
            }
        }

        if (this.userClientService.currentUser?.user && encryptedGuid && encryptedSubCorrespondenceId) {
            await this.taskService.acceptInvitation(encryptedGuid, encryptedSubCorrespondenceId);
        } else if (this.userClientService.currentUser?.user && encryptedEntityId && isForm) {
            await this.syncTaskService.syncTask(0, EntityTypes.VesselApplicationForms, encryptedEntityId);
        }

        //Init Intercom
        const user = await this.userClientService.getMe();
        const userResponse = await this.userClientService.getCurrentUser();
        this.intercomService.init(user, userResponse.intercomHash);

        //DataDog
        this.setUserForDataDog(this.userClientService.currentUser?.user);
    }

    detached() {

        //Dispose of all subscriptions
        this.subscriptions.forEach(x => {
            x.dispose();
        });
        this.subscriptions = [];
    }

    //Configure the router
    public configureRouter(config: RouterConfiguration, router: Router) {
        config.title = "My Tasks";
        config.options.pushState = true;
        config.options.hashChange = true;
        config.options.root = "/";

        //Add My Vessel app specific routes
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../dashboard/dashboard"), name: "dashboard", route: MyVesselsRouteConstants.Dashboard })
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("@rightship-tech/document-management-app/views/pages/manage-document-requirements/manage-document-requirements"), name: "manage-document-requirements", route: DocumentManagementRoutes.ManageDocumentRequirements })
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("@rightship-tech/document-management-app/views/pages/form-viewer/form-viewer"), name: "form-viewer", route: DocumentManagementRoutes.FormViewer })
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../rfi-correspondence/rfi-correspondence"), name: "rfi-correspondence", title: "RFI", route: MyVesselsRouteConstants.RfiCorrespondence })
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../vessel-documents/vessel-documents"), name: "vessel-documents", title: "Vessel Documents", route: MyVesselsRouteConstants.VesselDocuments })
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../tasks/all-tasks/all-tasks"), name: "all-tasks", title: "All tasks", route: MyVesselsRouteConstants.AllTasks })
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../common/submit-close-out-information/submit-close-out-information"), name: "submit-close-out-information", title: "Submit Close-Out Information", route: MyVesselsRouteConstants.SubmitCloseOutInformation })
        this.routeGenerator.addRoute({ moduleId: PLATFORM.moduleName("../common/select-vessel/select-vessel"), name: "select-vessel", title: "Select Vessel", route: MyVesselsRouteConstants.SelectVessel })
        
        //Fallback route
        this.routeGenerator.addRoute({ moduleId: "../not-found/not-found", name: "not-found", title: "Page not found", route: "*path" })

        //Add routes from other apps
        config.map(this.routeGenerator.getRoutes());
        this.routeGenerator.setRouter(router);
    }

    //Update Intercom
    public updateIntercom() {
        this.intercomService.update();
    }

    setUserForDataDog(userResponse: GetRightShipQiUserResponseUser) {
        let ddrum = window["DD_RUM"];
        if (ddrum && userResponse) {
            ddrum.setUser({
                id: userResponse.id,
                name: userResponse.fullName,
                email: userResponse.email
            });
        }
    }
}